import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[20]

export const KWP_20_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '20-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-20',
  title: '20 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 20-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für Zweifamilienhäuser mit hohem Verbrauch.',
  keywords: [
    '20 kWp Photovoltaik Kosten',
    '20 kWp Solaranlage Preis',
    'Photovoltaikanlage 20 kWp',
    'Was kostet eine 20 kWp Anlage',
    '20 kWp PV-Anlage Zweifamilienhaus',
  ],
  eyebrow: 'Anlagengröße 20 kWp',
  h1: '20 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 20-kWp-Anlage eignet sich für Zweifamilienhäuser oder Häuser mit sehr hohem ' +
    'Gesamtverbrauch. Hier finden Sie Richtwerte zu Dachfläche und Modulanzahl, eine ' +
    'Kostenaufschlüsselung und direkt darunter einen Rechner mit dem für 20 kWp vorbelegten ' +
    'Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 20-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 20 kWp passen typischerweise zu einem Zweifamilienhaus mit zwei separaten ' +
        'Haushalten oder einem Einfamilienhaus mit sehr hohem Gesamtverbrauch (Wärmepumpe, ' +
        'Elektroauto, ggf. Pool), Jahresverbrauch insgesamt oft im Bereich von 12.000-16.000 kWh.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 20-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Bei dieser Größe reicht oft ` +
        'nicht mehr nur eine einzelne Dachfläche - eine genaue, dachspezifische Berechnung mit ' +
        'mehreren Ausrichtungen liefert der kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 20 kWp meist mehrere Strings, teils mehrere Wechselrichter',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung, bei mehreren Dachflächen entsprechend mehr Aufwand',
        'Gerüst: bei größerer Dachfläche entsprechend umfangreicher',
        'Elektroinstallation: Zählerschrank-Anpassung, ggf. separate Zähler bei zwei Haushalten',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Bei einem Zweifamilienhaus mit zwei separaten Haushalten klärt der Handwerksbetrieb im ' +
        'Angebot zusätzlich, wie Erzeugung und Verbrauch zwischen beiden Parteien technisch und ' +
        'messtechnisch aufgeteilt werden.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        '20 kWp liegt weiterhin unterhalb der 30-kWp-Grenze, bis zu der Einnahmen aus ' +
        'Photovoltaikanlagen vollständig einkommensteuerbefreit sind und 0 % Mehrwertsteuer auf ' +
        'Kauf und Installation gilt (Details im Förder-Überblick). Eine genaue Berechnung von ' +
        'Eigenverbrauch und Amortisation für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Ändert sich bei einer 20-kWp-Anlage etwas steuerlich oder rechtlich?',
      a: 'Nein - 20 kWp liegt weiterhin unterhalb der 30-kWp-Grenze für die vollständige Einkommensteuerbefreiung und die 0 % Mehrwertsteuer auf Kauf und Installation. Details im Förder-Überblick.',
    },
    {
      q: 'Eignet sich 20 kWp für ein Zweifamilienhaus?',
      a: 'Ja, das ist einer der typischen Anwendungsfälle für diese Größe. Der Handwerksbetrieb klärt im Angebot zusätzlich, wie Erzeugung und Verbrauch zwischen den beiden Haushalten aufgeteilt werden.',
    },
    {
      q: 'Reicht eine einzelne Dachfläche für 20 kWp?',
      a: `Richtwert: rund ${FACTS.roofAreaM2} m² werden benötigt - bei kleineren oder ungünstig geschnittenen Dächern verteilen Handwerksbetriebe die Module oft auf mehrere Dachflächen mit unterschiedlicher Ausrichtung.`,
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '15 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/15-kwp-photovoltaik-kosten' },
    { label: '25 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/25-kwp-photovoltaik-kosten' },
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: '20 kWh Batteriespeicher: Kosten & Richtwerte', href: '/20-kwh-speicher-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
